import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, Home as HomeIcon, Mail, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";

const STEP_ICONS = { paid: CheckCircle2, processing: Package, shipped: Truck, delivered: HomeIcon };

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const location = useLocation();
  const { format } = useCurrency();
  const [order, setOrder] = useState(location.state?.order || null);

  useEffect(() => {
    if (!order) api.get(`/orders/track?order=${orderId}`).then(({ data }) => setOrder(data)).catch(() => setOrder(false));
  }, [orderId, order]);

  if (order === null) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-[#00F0FF] border-t-transparent" /></div>;
  if (order === false) return <div className="mx-auto max-w-2xl px-4 py-20 text-center text-slate-400">Order not found.</div>;

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#00E676]/10 glow-cyan">
          <CheckCircle2 className="h-10 w-10 text-[#00E676]" />
        </div>
        <h1 className="font-display mt-6 text-3xl font-bold text-white" data-testid="order-confirmed-title">Order Confirmed!</h1>
        <p className="mt-2 text-slate-400">Thanks for your order. A confirmation email is on its way to <span className="text-slate-200">{order.email}</span>.</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 font-mono text-sm text-[#00F0FF]" data-testid="order-id">Order #{order.order_id}</div>
      </motion.div>

      <div className="dw-card mt-10 rounded-2xl p-6">
        <h2 className="font-display text-lg font-semibold text-white">Order Timeline</h2>
        <div className="mt-6 space-y-6">
          {order.timeline?.map((s, i) => {
            const Icon = STEP_ICONS[s.status] || Package;
            return (
              <div key={i} className="flex items-start gap-4">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${s.done ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-white/5 text-slate-600"}`}><Icon className="h-5 w-5" /></div>
                <div className="flex-1 pt-1">
                  <p className={`text-sm font-semibold ${s.done ? "text-slate-100" : "text-slate-500"}`}>{s.label}</p>
                  {s.at && <p className="font-mono text-xs text-slate-500">{new Date(s.at).toLocaleString()}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="dw-card mt-6 rounded-2xl p-6">
        <h2 className="font-display text-lg font-semibold text-white">Items</h2>
        <div className="mt-4 space-y-3">
          {order.items?.map((i) => (
            <div key={i.product_id} className="flex items-center gap-3">
              <div className="h-12 w-12 overflow-hidden rounded-lg bg-[#0B0D16]"><img src={i.image} alt="" className="h-full w-full object-cover opacity-90" /></div>
              <span className="flex-1 text-sm text-slate-200">{i.name} × {i.quantity}</span>
              <span className="font-mono text-sm text-slate-300">{format(i.price_usd * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-between border-t border-white/10 pt-4 text-base font-bold text-white"><span>Total</span><span className="font-mono text-[#00F0FF]">{format(order.total_usd)}</span></div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Link to={`/track?order=${order.order_id}`} className="flex-1"><Button className="w-full rounded-xl bg-[#00F0FF] py-6 font-bold text-[#08090E] hover:bg-[#33F3FF]" data-testid="track-order-btn"><Truck className="mr-2 h-4 w-4" /> Track Order</Button></Link>
        <Link to="/shop" className="flex-1"><Button variant="outline" className="w-full rounded-xl border-white/15 bg-transparent py-6 text-slate-100 hover:bg-white/5" data-testid="continue-shopping-btn">Continue Shopping <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
      </div>
    </div>
  );
}
